import axios from 'axios';

const DEFAULT_COLORS = { r: '#ff0000', g: '#00ff00', b: '#0000ff' };

const makeDefaultParameter = (id, index) => ({
  id,
  title: `Measuring parameter ${index + 1}`,
  enabled: false,
  color: DEFAULT_COLORS[id],
  signal: '',
  normalization: '',
  calibrationId: '',
  formula: '',
  label: '',
  data: [],
  labels: [],
  is_filter: false,
  filter_min: null,
  filter_max: null,
});

const state = {
  parameters: [
    makeDefaultParameter('r', 0),
    makeDefaultParameter('g', 1),
    makeDefaultParameter('b', 2),
  ],
  appliedAt: null,
  // Стартовое время диапазона на момент Apply. Авто-графики используют его как
  // собственный left-край live-опроса, чтобы Add chart (сдвигающий глобальный
  // dateStart на «сейчас») не перетирал окно уже построенных графиков.
  appliedStartTime: null,
  // Логарифмическая шкала значений для авто-графиков. Снимок флага
  // log scale с вкладки Map в момент нажатия Apply.
  logScale: false,
};

const getters = {
  chartsParameters: state => state.parameters,
  chartsAppliedAt: state => state.appliedAt,
  chartsAppliedStartTime: state => state.appliedStartTime,
  chartsEnabledParameters: state => state.parameters.filter(p => p.enabled),
  chartsLogScale: state => state.logScale,
};

const mutations = {
  SET_CHARTS_PARAMETERS(state, parameters) {
    state.parameters = parameters.map((p, i) => {
      const defined = Object.fromEntries(
        Object.entries(p).filter(([, v]) => v !== undefined)
      );
      return { ...makeDefaultParameter(p.id, i), ...defined };
    });
  },
  SET_CHARTS_PARAM_DATA(state, { id, data, labels }) {
    const param = state.parameters.find(p => p.id === id);
    if (!param) return;
    param.data = data || [];
    param.labels = labels || [];
  },
  SET_CHARTS_APPLIED_AT(state, ts) {
    state.appliedAt = ts;
  },
  SET_CHARTS_APPLIED_START_TIME(state, ts) {
    state.appliedStartTime = ts || null;
  },
  SET_CHARTS_LOG_SCALE(state, flag) {
    state.logScale = !!flag;
  },
  CLEAR_CHARTS_PARAMETERS(state) {
    state.parameters = state.parameters.map((p, i) => makeDefaultParameter(p.id, i));
    state.appliedAt = null;
    state.appliedStartTime = null;
    state.logScale = false;
  },
  SET_CHARTS_PARAM_FIELDS(state, { id, fields }) {
    const param = state.parameters.find(p => p.id === id);
    if (!param) return;
    Object.entries(fields).forEach(([key, value]) => {
      param[key] = value;
    });
  },
  DISABLE_CHARTS_PARAM(state, id) {
    const param = state.parameters.find(p => p.id === id);
    if (!param) return;
    param.enabled = false;
    param.is_filter = false;
    param.filter_min = null;
    param.filter_max = null;
  },
};

const actions = {
  async applyChartsParameters({ commit, state, rootState }, { parameters, fetchOptions, logScale }) {
    commit('SET_CHARTS_PARAMETERS', parameters);
    commit('SET_CHARTS_LOG_SCALE', logScale);
    commit('SET_CHARTS_APPLIED_START_TIME', fetchOptions ? fetchOptions.startTime : null);

    // Тип лазера общий для всех вкладок и живёт в модуле results.
    const laser = (rootState.results && rootState.results.laser) || '';

    const enabled = state.parameters.filter(p => p.enabled && p.formula);
    const requests = enabled.map(async (param) => {
      try {
        const requestParams = {
          start_time: fetchOptions.startTime,
          end_time: fetchOptions.endTime,
          avg_time: fetchOptions.avgTime,
          param: param.formula,
        };
        // Применяем калибровочный полином по выбранной кривой Measuring parameter.
        if (param.calibrationId) requestParams.calibration_id = param.calibrationId;
        // Фильтр по типу лазера (пустое значение не шлём).
        if (laser) requestParams.laser = laser;
        // Data limits (то же, что Data limits на Map): фильтрация по значению
        // на стороне бэкенда — точки вне [min, max] выбрасываются.
        if (param.is_filter) {
          if (param.filter_min !== null && param.filter_min !== undefined && param.filter_min !== '') {
            requestParams.min_value = param.filter_min;
          }
          if (param.filter_max !== null && param.filter_max !== undefined && param.filter_max !== '') {
            requestParams.max_value = param.filter_max;
          }
        }
        const response = await axios.get('measurements/param', {
          params: requestParams,
        });
        commit('SET_CHARTS_PARAM_DATA', {
          id: param.id,
          data: response.data.values,
          labels: response.data.times,
        });
      } catch (err) {
        console.error(`Failed to fetch chart data for parameter ${param.id}:`, err);
        commit('SET_CHARTS_PARAM_DATA', { id: param.id, data: [], labels: [] });
      }
    });

    await Promise.all(requests);
    commit('SET_CHARTS_APPLIED_AT', Date.now());
  },
  clearChartsParameters({ commit }) {
    commit('CLEAR_CHARTS_PARAMETERS');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
